import {
  Box,
  Button,
  Tooltip,
  Text,
  TextProps,
  ButtonProps,
} from '@frontend/components';

import { useAppSelector } from 'store/hooks';
import { getActiveGroup } from 'store/session/selectors';

import { useUserGroupCollectionContext } from 'utils/multisite';

import { buildHighlightOutput } from '../utils/buildHighlightOutput';

export type HighlightOrDisplayProps = {
  allowNestedLink?: boolean;
  field: string;
  result: any;
  useDataFont?: boolean;
  nonHighlightTextColor?: string;
  textProps?: ButtonProps & TextProps;
  showOnlyIfHighlighted?: boolean;
  resultOwnerId?: string;
};

const HighlightOrDisplay = ({
  allowNestedLink,
  field,
  result,
  useDataFont = false,
  nonHighlightTextColor = 'feature.primary',
  textProps = {},
  showOnlyIfHighlighted = false,
  resultOwnerId,
  ...rest
}: HighlightOrDisplayProps) => {
  const activeGroup = useAppSelector(getActiveGroup);
  const ownerId = resultOwnerId ?? result.data.ownerId;
  const isFromOutsideGroup = ownerId && ownerId !== activeGroup.id;
  const { initChangeSite } = useUserGroupCollectionContext();
  const linkProps = isFromOutsideGroup
    ? {
        onClick: () =>
          initChangeSite({
            nextUrl: result.path,
            userGroupId: ownerId,
          }),
      }
    : { href: result.path };

  if (result.highlight?.[field]) {
    const rawFieldData = result.data[field];
    const fieldData = Array.isArray(rawFieldData)
      ? rawFieldData
      : [rawFieldData];
    const output = buildHighlightOutput(fieldData, result.highlight[field]);
    return (
      <Box display="inline" {...rest}>
        {output.map(({ data }, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={idx}>
            {data.map(({ text, type }, tokenIdx) =>
              type === 'highlight' ? (
                allowNestedLink && result.path ? (
                  <Tooltip
                    // eslint-disable-next-line react/no-array-index-key
                    key={tokenIdx}
                    hasArrow
                    label="Click to view"
                    placement="bottom"
                  >
                    <Button
                      as="a"
                      variant="link"
                      fontSize="sm"
                      fontFamily={useDataFont ? 'data' : 'default'}
                      {...linkProps}
                      {...textProps}
                    >
                      {text}
                    </Button>
                  </Tooltip>
                ) : (
                  <Text
                    as="span"
                    display="inline"
                    color="brand.secondary"
                    fontWeight="heavy"
                    fontSize="sm"
                    // eslint-disable-next-line react/no-array-index-key
                    key={tokenIdx}
                    fontFamily={useDataFont ? 'data' : 'default'}
                    {...textProps}
                  >
                    {text}
                  </Text>
                )
              ) : (
                <Text
                  as="span"
                  fontSize="sm"
                  // eslint-disable-next-line react/no-array-index-key
                  key={tokenIdx}
                  fontFamily={useDataFont ? 'data' : 'default'}
                  color={nonHighlightTextColor}
                  {...textProps}
                >
                  {text}
                </Text>
              ),
            )}
            {idx < output.length - 1 ? `, ` : null}
          </span>
        ))}
      </Box>
    );
  } else {
    if (showOnlyIfHighlighted) {
      return null;
    }

    let resultText = result.data[field] ?? '--';
    if (Array.isArray(resultText)) {
      resultText = resultText.join(', ');
    }
    return (
      <Text
        as="span"
        fontSize="sm"
        fontFamily={useDataFont ? 'data' : 'default'}
        color={nonHighlightTextColor}
        {...textProps}
        {...rest}
      >
        {resultText}
      </Text>
    );
  }
};

export default HighlightOrDisplay;
